import { useEffect, useState } from 'react';
import { Box, Button, Divider, FormControl, FormLabel, Input, Textarea, Typography, Select, Option, Sheet, Modal, ModalDialog, ModalClose, useTheme } from '@mui/joy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/joy/IconButton';
import { supabase } from '../supabaseClient';
import dayjs from 'dayjs';
import PageLayout from '../components/PageLayout';
import StyledTable from '../components/StyledTable';

export default function ClientIntDates() {
  const theme = useTheme();
  const [projects, setProjects] = useState([]);
  const [clientDates, setClientDates] = useState([]);
  const [form, setForm] = useState({
    client_id: '',
    project_id: '',
    date: '',
    description: '',
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => {
    fetchProjects();
    fetchClientDates();
  }, []);

  async function fetchProjects() {
    const { data, error } = await supabase
      .from('projects')
      .select('id, name, client_id, clients(name)');
    if (error) console.error(error);
    else setProjects(data);
  }

  async function fetchClientDates() {
    const { data, error } = await supabase
      .from('client_dates')
      .select(`
        id,
        date,
        description,
        project_id,
        client_id,
        clients ( name ),
        projects ( id, name )
      `)
      .order('date', { ascending: true });
    if (error) console.error(error);
    else setClientDates(data);
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    const operation = editId
      ? supabase.from('client_dates').update(form).eq('id', editId)
      : supabase.from('client_dates').insert([form]);

    const { error } = await operation;
    if (error) {
      console.error(error);
    } else {
      setForm({ client_id: '', project_id: '', date: '', description: '' });
      setEditId(null);
      fetchClientDates();
      setModalOpen(false);
    }
  };

  const handleEdit = (date) => {
    setForm({
      client_id: date.client_id,
      project_id: date.project_id,
      date: date.date,
      description: date.description,
    });
    setEditId(date.id);
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this date?')) return;
    const { error } = await supabase.from('client_dates').delete().eq('id', id);
    if (error) console.error(error);
    else fetchClientDates();
  };

  const groupedByClient = clientDates.reduce((acc, date) => {
    const clientName = date.clients?.name ?? 'Unknown Client';
    const projectName = date.projects?.name ?? 'Unknown Project';

    if (!acc[clientName]) acc[clientName] = {};
    if (!acc[clientName][projectName]) acc[clientName][projectName] = [];
    acc[clientName][projectName].push(date);

    return acc;
  }, {});

  const filteredProjects = form.client_id ? projects.filter(p => p.client_id === form.client_id) : [];

  return (
    <PageLayout title="Client Dates Admin">
      <Box sx={{ p: 3 }}>
        <Button
          variant="solid"
          color="primary"
          sx={{ mb: 3 }}
          onClick={() => {
            setEditId(null);
            setForm({ client_id: '', project_id: '', date: '', description: '' });
            setModalOpen(true);
          }}
        >
          Add Client Date
        </Button>

        {Object.entries(groupedByClient).map(([client, projects]) => (
          <Box key={client} sx={{ mb: 5 }}>
            <Typography level="h5" sx={{ mb: 2, color: theme.palette.text.primary }}>{client}</Typography>
            {Object.entries(projects).map(([project, dates]) => {
              const columns = [
                { key: 'date', label: 'Date' },
                { key: 'description', label: 'Description' },
                { key: 'actions', label: '' },
              ];
              const rows = dates.map(date => ({
                id: date.id,
                date: dayjs(date.date).format('MMM D, YYYY'),
                description: date.description,
                actions: (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton size="sm" onClick={() => handleEdit(date)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="sm" color="danger" onClick={() => handleDelete(date.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ),
              }));
              return (
                <Box key={project} sx={{ mb: 4 }}>
                  <Typography level="body-lg" sx={{ fontWeight: 'lg', mb: 1 }}>{project}</Typography>
                  <StyledTable columns={columns} rows={rows} />
                </Box>
              );
            })}
            <Divider sx={{ mt: 2, mb: 2 }} />
          </Box>
        ))}

        <Modal open={modalOpen} onClose={() => setModalOpen(false)}>
          <ModalDialog
            aria-labelledby="client-date-modal-title"
            aria-describedby="client-date-modal-description"
            sx={{ maxWidth: 500 }}
          >
            <ModalClose />
            <Typography id="client-date-modal-title" component="h2" level="h4" mb={2}>
              {editId ? 'Edit Client Date' : 'Add Client Date'}
            </Typography>
            <form onSubmit={handleSubmit}>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Client</FormLabel>
                <Select
                  value={form.client_id}
                  onChange={(_, val) => setForm(prev => ({ ...prev, client_id: val, project_id: '' }))}
                  required
                >
                  {[...new Map(projects.map(p => [p.client_id, p.clients.name])).values()].map((clientName, index) => {
                    const clientId = [...new Map(projects.map(p => [p.client_id, p.clients.name]))].find(([, name]) => name === clientName)[0];
                    return (
                      <Option key={clientId} value={clientId}>
                        {clientName}
                      </Option>
                    );
                  })}
                </Select>
              </FormControl>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Project</FormLabel>
                <Select
                  value={form.project_id}
                  onChange={(_, val) => setForm(prev => ({ ...prev, project_id: val }))}
                  required
                  disabled={!form.client_id}
                >
                  {filteredProjects.map(project => (
                    <Option key={project.id} value={project.id}>
                      {project.name}
                    </Option>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Date</FormLabel>
                <Input
                  type="date"
                  value={form.date}
                  onChange={e => setForm(prev => ({ ...prev, date: e.target.value }))}
                  required
                />
              </FormControl>
              <FormControl sx={{ mb: 2 }}>
                <FormLabel>Description</FormLabel>
                <Textarea
                  minRows={2}
                  value={form.description}
                  onChange={e => setForm(prev => ({ ...prev, description: e.target.value }))}
                  required
                />
              </FormControl>
              <Button type="submit" variant="solid" color="primary" fullWidth>
                {editId ? 'Update Date' : 'Add Date'}
              </Button>
            </form>
          </ModalDialog>
        </Modal>
      </Box>
    </PageLayout>
  );
}